<template>
<div>
  <!-- Estadísticas -->
  <BRow>
    <BCol cols="12" class="p-1">
      <BCard v-if="generalStats" no-body class="card-statistics h-100 ">
        <BCardHeader>
          <div>
            <BCardTitle>{{ $t('Statistics') }}</BCardTitle>
            <BCardText class="font-small-2 mr-25 mb-0"> {{ $t('Updated recently') }} </BCardText>
          </div>
          <VSelectValidated vsName="" :vsModel.sync="selectedGroup" :vsReduce="group=>group.id" :vsOptions="groupsOptions" vsLabel="name" vsMd="3" :vsDisabled="groupsOptions.length == 1 ? true : false" />
        </BCardHeader>
        <BCardBody class="statistics-body">
          <BRow>
            <BCol class="pt-2" v-for="item in generalStats" :key="item.icon" xl="3" sm="6" :class="item.customClass" >
              <BMedia no-body>
                <BMediaAside class="mr-2">
                  <BAvatar size="48" :variant="item.color" >
                    <feather-icon size="24" :icon="item.icon" />
                  </BAvatar>
                </BMediaAside>
                <BMediaBody class="my-auto">
                  <h4 class="font-weight-bolder mb-0"> {{ item.title }} </h4>
                  <BCardText class="font-small-3 mb-0"> {{ $t(item.subtitle) }} </BCardText>
                </BMediaBody>
              </BMedia>
            </BCol>
          </BRow>
        </BCardBody>
      </BCard>
    </BCol>

    <!-- Resumen anual -->
    <BCol v-if="ability.can('read', 'report')" cols="12" class="p-1" >
      <IncomesChart :data="barChartData" @refetch-revenues="fetchRevenues"/>
    </BCol>

  <!-- Últimos cobros -->
  <BCol cols="6" class="p-1">
    <BCard no-body class="card-statistics h-100 ">
      <BCardHeader>
        <BCardTitle> Últimos cobros de facturas</BCardTitle>
      </BCardHeader>
      <BCardBody class="statistics-body">
        <BRow>
          <BCol>
            <BTable v-if="charges" ref="chargesTable" id="chargesTable" striped :items="charges" :fields="chargesFields" responsive show-empty empty-text="Ningún cobro"> 
              <template #cell(customer)="data">
                <span class="text-nowrap"> {{ data.item.invoice.customer_billing_full_name }} </span>
              </template> 
              <template #cell(invoice)="data">
                <BLink :to="{ name: 'invoices-view', params: { id: data.item.invoice_id } }"> {{ data.item.invoice.name }} </BLink>
              </template>      
              <template #cell(amount)="data">
                <span class="text-nowrap"> {{ formatCurrency(data.item.amount) }} </span>
              </template> 
              <template #cell(payment_method)="data">
                <span> {{ data.item.payment_method.name }} </span>
              </template> 
            </BTable>
          </BCol>
        </BRow>
      </BCardBody>
    </BCard>
  </BCol>

  <!-- Últimas matrículas -->
  <BCol cols="6" class="p-1">
    <BCard no-body class="card-statistics h-100 " >
      <BCardHeader>
        <BCardTitle> Últimas matrículas creadas</BCardTitle>
      </BCardHeader>
      <BCardBody class="statistics-body ">
        <BRow>
          <BCol>
            <BTable v-if="enrollments" ref="enrollmentsTable" id="enrollmentsTable" striped :items="enrollments" :fields="enrollmentsFields" responsive show-empty empty-text="Ninguna matrícula"> 

              <template #cell(code)="data">
                <BLink :to="{ name: 'enrollments-view', params: { id: data.item.id } }" class="font-weight-bold d-block text-nowrap" > {{ data.item.code }} </BLink>
              </template>
              <template #cell(student)="data">
                <span> {{ data.item.student.full_name }} </span>
              </template> 
              <template #cell(invoice)="data">
                <BLink :to="{ name: 'invoices-view', params: { id: data.item.invoice_id } }"> {{ data.item.invoice.name }} </BLink>
              </template>      
              <template #cell(start_date)="data">
                <span> {{ formatDate(data.item.start_date) }} </span>
              </template> 
            </BTable>
          </BCol>
        </BRow>
      </BCardBody>
    </BCard>
  </BCol>


  <!-- Próximos cursos que empezarán -->

  <BCol cols="6" class="p-1">
    <BCard v-if="courses" class="card-transaction h-100" no-body >
      <BCardHeader>
        <BCardTitle>Próximos cursos que empezarán</BCardTitle>
      </BCardHeader>

      <BCardBody>
        <div v-for="course in courses" :key="course.mode" class="course-item d-flex justify-content-between mt-2" >
            <!-- <BMediaAside>
              <b-avatar rounded size="42" variant="danger" >
                <feather-icon size="18" icon="trashIcon" />
              </b-avatar>
            </BMediaAside> -->
          <div>
            <BLink :to="{ name: 'courses-view', params: { id: course.id } }"> {{ course.name }} </BLink>
          </div>
          <div class="font-weight-bolder" > 
             <BBadge  pill variant="light-danger" > En {{ course.days }} días</BBadge>
          </div>
        </div>
      </BCardBody>
    </BCard>
  </BCol>

  <BCol cols="4" class="p-1">
    <RadialBarChart :data="radialChartData"/>
  </BCol>
  

</BRow>



</div>
</template>

<script>
import { BCard, BCardHeader, BCardTitle, BCardText, BCardBody, BRow, BCol, BMedia, BMediaAside, BAvatar, BMediaBody, BTable, BLink, BBadge} from 'bootstrap-vue'
import axios from '@axios'
import ability from '@/libs/acl/ability'
import { ref, watch, provide } from '@vue/composition-api'
import IncomesChart from '@/components/stats/IncomesChart.vue';
import RadialBarChart from '@/components/stats/RadialBarChart.vue';
import currency from '@/utils/currency'
import VSelectValidated from '@/components/forms/vSelectValidated.vue';
import { getGroups } from '@/composables/groupService'
import dateUtils from '@/hooks/dateUtils'


export default {
  components: { BRow, BCol, BCard, BCardHeader, BCardTitle, BCardText, BCardBody, BMedia, BAvatar, BMediaAside, BMediaBody, IncomesChart, BTable, BLink, BBadge, RadialBarChart, VSelectValidated },
  setup(){
    const { formatCurrency } = currency()
    const { formatDate } = dateUtils()
    const generalStats = ref(null)
    const charges = ref(null)
    const enrollments = ref(null)
    const courses = ref(null)
    const radialChartData = ref(null)
    const barChartData = ref(null)
    const selectedYear = ref(((new Date()).getFullYear()).toString())
    const currentPage=ref(1)
    const groupsOptions =ref([])
    const selectedGroup = ref("all")
    const chargesFields = [
      { key:'invoice', label: 'Factura' },
      { key:'customer', label: 'Cliente' },
      { key:'amount', label: 'Importe' },
      { key:'payment_method', label: 'Método de pago' },
    ]
    const enrollmentsFields = [
      { key:'code', label: 'Código' },
      { key:'student', label: 'Estudiante' },
      { key:'start_date', label: 'Comienzo' },
    ]

    provide('selectedYear', selectedYear)

    const fetchGeneral = () => {
      axios.post('/home-stats-general', {group: selectedGroup.value}).then(response =>{
          generalStats.value = response.data
      })
    }

    const fetchInvoices = () => {
      axios.post('/home-stats-invoices', {group: selectedGroup.value}).then(response =>{
          charges.value = response.data.charges
      })
    }

    const fetchEnrollments = () => {
      axios.post('/home-stats-enrollments', {group: selectedGroup.value}).then(response =>{
        enrollments.value = response.data.enrollments
      })
    }

    const fetchCourses = () => {
      axios.post('/home-stats-courses', {group: selectedGroup.value}).then(response =>{
        courses.value = response.data.courses
      })
    }

    const fetchPercentage = () => {
      axios.post('/home-paid-receipts', {group: selectedGroup.value}).then(response =>{
        radialChartData.value = response.data
      })
    }

    const fetchRevenues = () => {
      axios.post('/home-revenue-report', {group: selectedGroup.value, year: selectedYear.value}).then(response =>{
        barChartData.value = response.data
      })
    }

    const fetchGroups = async function() {
      groupsOptions.value = (await getGroups()).data.groups
      if (groupsOptions.value.length == 1 ) {
        selectedGroup.value=groupsOptions.value[0].id;
      } else {
        groupsOptions.value.push({name: "Todas", id:"all"})
        selectedGroup.value="all"
      }


    }
    
    fetchGroups()
    fetchGeneral()
    fetchInvoices()
    fetchEnrollments()
    fetchCourses()
    fetchPercentage()
    fetchRevenues()

    watch(selectedGroup, () => {
      fetchGeneral()
      fetchInvoices()
      fetchEnrollments()
      fetchCourses()
      fetchPercentage()
      fetchRevenues()
  
    }, {deep:true}
    )

    return {
      generalStats,
      ability,
      charges,
      chargesFields,
      enrollments,
      enrollmentsFields,
      courses,
      formatCurrency,
      radialChartData,
      barChartData,
      groupsOptions,
      selectedGroup,
      fetchRevenues,
      formatDate
    }
  }
}
</script>
